import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router";
import { ArrowUpDown, Check, ChevronDown, ChevronUp, Filter } from "lucide-react";
import { cn } from "@/lib/cn";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/**
 * Sortable, filterable table for dashboard cards. Every table on a dashboard
 * uses this shell so the behaviour is the same everywhere:
 *
 * - **Sorting is always on.** Click a header to sort descending, click again
 *   for ascending, a third click drops back to the query's own order. The
 *   active column and direction live in the URL, so a shared link reproduces
 *   the exact view.
 * - **Filtering is for finite columns only.** A column whose values form a
 *   small closed set (status, lab, platform, Ja/Nei) is marked
 *   `filterable: true` and gets a checkbox menu in its header, listing the
 *   values actually present in the rows. Arbitrary numbers — amounts, counts,
 *   dates — are not filterable, and columns the page already filters on
 *   (the date range, the product multi-select, the channel chips) do not get
 *   a second, competing filter in the header.
 *
 * Column filters and the sort are client-side: chart queries return their
 * whole row set in one go, and filtering in JS is what keeps a filtered view
 * instant and shareable.
 */

export type TableCellValue = string | number | boolean | null | undefined;

export interface DashboardTableColumn<T> {
  /** Unique within the table; also used in the URL param names. */
  id: string;
  /** Header label. May carry a <Hint> with the column's explanation. */
  label: React.ReactNode;
  /**
   * The value a row carries in this column, for sorting and filtering.
   * Required on sortable and filterable columns; a `sortable: false` action
   * column has none. null / undefined / "" sort last under any direction and
   * never appear as a filter option.
   */
  value?: (row: T) => TableCellValue;
  /** Numeric compare. Postgres sends numbers as strings, so set this on every number column. */
  numeric?: boolean;
  align?: "left" | "right";
  /** Finite set of options → the header gets a filter menu. */
  filterable?: boolean;
  /** Display label for a raw option in the filter menu. Defaults to the raw value. */
  formatOption?: (value: string) => string;
  /** Plain-text name for a11y (aria labels). Defaults to `id`. */
  labelText?: string;
  /** Extra classes on the header cell (e.g. column-group rules). */
  headClassName?: string;
  /** Opt out of sorting — action columns and the row key of a breakdown. */
  sortable?: boolean;
}

/**
 * Ordering for one value pair. Empty values sink last regardless of
 * direction — an empty date is "unknown", not "earliest", and floating
 * unknowns to the top buries the rows you actually want to look at.
 */
export function compareTableValues(
  a: TableCellValue,
  b: TableCellValue,
  numeric = false,
): number {
  const aEmpty = a == null || a === "";
  const bEmpty = b == null || b === "";
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (typeof a === "boolean" || typeof b === "boolean") {
    const an = typeof a === "boolean" ? (a ? 1 : 0) : 0;
    const bn = typeof b === "boolean" ? (b ? 1 : 0) : 0;
    return an - bn;
  }
  if (numeric) {
    const an = Number(a);
    const bn = Number(b);
    if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
  }
  return String(a).localeCompare(String(b), "no");
}

/** A sorted copy of `rows`. `column` null/absent keeps the given order. */
export function sortTableRows<T>(
  rows: readonly T[],
  column: DashboardTableColumn<T> | null | undefined,
  dir: "asc" | "desc" = "desc",
): T[] {
  if (!column || !column.value) return [...rows];
  const value = column.value;
  const factor = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = value(a);
    const bv = value(b);
    // Empty values sink last in both directions: the direction flip applies
    // to real values only, or a descending sort would float the unknowns to
    // the top and bury the rows you actually want to look at.
    const aEmpty = av == null || av === "";
    const bEmpty = bv == null || bv === "";
    if (aEmpty || bEmpty) return aEmpty && bEmpty ? 0 : aEmpty ? 1 : -1;
    const cmp = compareTableValues(av, bv, column.numeric);
    return cmp === 0 ? 0 : cmp * factor;
  });
}

/**
 * Rows kept by the active column filters. Columns with no selected values
 * pass everything through; a row is kept only when it matches every active
 * filter, and a null value can never match one.
 */
export function filterTableRows<T>(
  rows: readonly T[],
  columns: readonly DashboardTableColumn<T>[],
  filters: Record<string, string[]>,
): T[] {
  const active = columns.filter(
    (c) => c.filterable && (filters[c.id]?.length ?? 0) > 0,
  );
  if (active.length === 0) return [...rows];
  return rows.filter((row) =>
    active.every((c) => {
      const v = c.value ? c.value(row) : null;
      return v != null && v !== "" && (filters[c.id] ?? []).includes(String(v));
    }),
  );
}

export interface TableColumnOption {
  value: string;
  label: string;
  count: number;
}

/**
 * The distinct values a column actually holds, with how many rows each
 * covers. Computed over the unfiltered rows, so unchecking one option never
 * makes the others disappear from the menu.
 */
export function columnOptions<T>(
  rows: readonly T[],
  column: DashboardTableColumn<T>,
): TableColumnOption[] {
  const counts = new Map<string, number>();
  const value = column.value;
  if (!value) return [];
  for (const row of rows) {
    const v = value(row);
    if (v == null || v === "") continue;
    const key = String(v);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([value, count]) => ({
      value,
      label: column.formatOption ? column.formatOption(value) : value,
      count,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, "no"));
}

/**
 * An extra <thead> row above the column header — the column-group labels of
 * the grouped tables ("Så langt", "Kostnad per helsesjekk"). The cells flow
 * left to right and should cover the table's columns; a cell without `text`
 * is a spacer over the columns that carry no group.
 */
export interface DashboardTableHeaderRow {
  cells: Array<{
    colSpan: number;
    text?: React.ReactNode;
    /** Left rule — the first column of a group, in header and body alike. */
    divide?: boolean;
  }>;
}

export interface DashboardTableState<T> {
  /** The rows after the column filters and the sort — what the table shows, and what an export should carry. */
  viewRows: T[];
  /** Active sort column id, "" for the query's own order. */
  sort: string;
  dir: "asc" | "desc";
  toggleSort: (columnId: string) => void;
  /** Selected option values per filterable column. */
  filters: Record<string, string[]>;
  setFilter: (columnId: string, values: string[]) => void;
  clearFilters: () => void;
  options: Record<string, TableColumnOption[]>;
  hasActiveFilters: boolean;
}

/**
 * URL-synced sort and column filters for one table on a dashboard.
 *
 * The sort is `sort_<id>` + `dir_<id>`; each filter is a repeated
 * `flt_<id>_<col>` param, one append per selected value. All of them use
 * empty defaults — absent means "no state", the same snapshot rules as
 * `useDashboardUrlState`, only with per-table param names so one page can
 * host several tables without the dashboards having to declare them.
 */
export function useDashboardTable<T>(
  id: string,
  columns: readonly DashboardTableColumn<T>[],
  rows: readonly T[] | null | undefined,
): DashboardTableState<T> {
  const [params, setParams] = useSearchParams();

  const sort = params.get(`sort_${id}`) ?? "";
  const dir = params.get(`dir_${id}`) === "asc" ? "asc" : "desc";

  const filters = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const c of columns) {
      if (c.filterable) out[c.id] = params.getAll(`flt_${id}_${c.id}`);
    }
    return out;
  }, [params, columns, id]);

  const toggleSort = useCallback(
    (columnId: string) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const sortKey = `sort_${id}`;
          const dirKey = `dir_${id}`;
          if (next.get(sortKey) === columnId) {
            if (next.get(dirKey) === "asc") {
              // Third click drops back to the query's own order.
              next.delete(sortKey);
              next.delete(dirKey);
            } else {
              next.set(dirKey, "asc");
            }
          } else {
            next.set(sortKey, columnId);
            next.set(dirKey, "desc");
          }
          return next;
        },
        { replace: true },
      );
    },
    [id, setParams],
  );

  const setFilter = useCallback(
    (columnId: string, values: string[]) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const key = `flt_${id}_${columnId}`;
          next.delete(key);
          for (const v of values) next.append(key, v);
          return next;
        },
        { replace: true },
      );
    },
    [id, setParams],
  );

  const clearFilters = useCallback(() => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const c of columns) next.delete(`flt_${id}_${c.id}`);
        return next;
      },
      { replace: true },
    );
  }, [id, setParams, columns]);

  const options = useMemo(() => {
    const out: Record<string, TableColumnOption[]> = {};
    if (rows) {
      for (const c of columns) {
        if (c.filterable) out[c.id] = columnOptions(rows, c);
      }
    }
    return out;
  }, [rows, columns]);

  const viewRows = useMemo(() => {
    if (!rows) return [];
    // A column without a value cannot be the sort key, even from a stale URL.
    const active = columns.find((c) => c.id === sort && c.value) ?? null;
    return sortTableRows(filterTableRows(rows, columns, filters), active, dir);
  }, [rows, columns, filters, sort, dir]);

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((values) => values.length > 0),
    [filters],
  );

  return {
    viewRows,
    sort,
    dir,
    toggleSort,
    filters,
    setFilter,
    clearFilters,
    options,
    hasActiveFilters,
  };
}

/**
 * The table itself. Call `useDashboardTable` in the dashboard and pass the
 * result in — the dashboard needs `viewRows` for its CSV export, so the
 * state cannot live inside this component.
 */
export function DashboardTable<T>({
  table,
  columns,
  renderRow,
  headerRows,
  footer,
}: {
  table: DashboardTableState<T>;
  columns: readonly DashboardTableColumn<T>[];
  /** One data row as a keyed <TableRow> (a <Fragment> of rows for drill-downs). `index` is in the filtered-and-sorted order. */
  renderRow: (row: T, index: number) => React.ReactNode;
  /** Column-group labels as extra <thead> rows above the column header. */
  headerRows?: DashboardTableHeaderRow[];
  /** Rendered inside the <table> after the body — totals rows. */
  footer?: React.ReactNode;
}) {
  const {
    viewRows,
    sort,
    dir,
    toggleSort,
    filters,
    setFilter,
    clearFilters,
    options,
  } = table;

  return (
    <Table>
      <TableHeader>
        {headerRows?.map((row, ri) => (
          <TableRow key={ri} className="border-b-0 hover:bg-transparent">
            {row.cells.map((cell, ci) => (
              <TableHead
                key={ci}
                colSpan={cell.colSpan}
                className={cn(
                  "h-7 px-3 pb-1 align-bottom text-center text-[11px] font-medium uppercase tracking-wide text-admin-text-subtle",
                  cell.divide && "border-l border-admin-card-border",
                )}
              >
                {cell.text}
              </TableHead>
            ))}
          </TableRow>
        ))}
        <TableRow>
          {columns.map((c) => (
            <TableHead
              key={c.id}
              className={cn(c.align === "right" && "text-right", c.headClassName)}
            >
              <span className="inline-flex items-center gap-0.5">
                {c.sortable === false ? (
                  c.label
                ) : (
                  <button
                    type="button"
                    onClick={() => toggleSort(c.id)}
                    title={
                      sort !== c.id
                        ? "Sorter synkende"
                        : dir === "desc"
                          ? "Sorter stigende"
                          : "Tilbake til standard rekkefølge"
                    }
                    className="inline-flex items-center gap-1 whitespace-nowrap hover:text-admin-text-primary"
                  >
                    {c.label}
                    <SortIcon active={sort === c.id} dir={dir} />
                  </button>
                )}
                {c.filterable && (options[c.id]?.length ?? 0) > 0 && (
                  <ColumnFilterMenu
                    ariaLabel={c.labelText ?? c.id}
                    options={options[c.id]!}
                    selected={filters[c.id] ?? []}
                    onChange={(values) => setFilter(c.id, values)}
                  />
                )}
              </span>
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {viewRows.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={columns.length}
              className="py-6 text-center text-sm text-admin-text-subtle"
            >
              Ingen rader matcher kolonnephiltrene.{" "}
              <button
                type="button"
                onClick={clearFilters}
                className="text-admin-primary hover:underline"
              >
                Nullstill filtre
              </button>
            </TableCell>
          </TableRow>
        ) : (
          viewRows.map((row, index) => renderRow(row, index))
        )}
      </TableBody>
      {footer}
    </Table>
  );
}

function SortIcon({ active, dir }: { active: boolean; dir: "asc" | "desc" }) {
  if (!active) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
  return dir === "asc" ? (
    <ChevronUp className="h-3 w-3" />
  ) : (
    <ChevronDown className="h-3 w-3" />
  );
}

/**
 * The checkbox menu behind a filterable header.
 *
 * Portalled to <body> and positioned fixed, for the same reason <Hint> is:
 * these headers sit inside the table's overflow container, which would clip
 * an absolutely positioned dropdown. Fixed does not follow its anchor, so
 * any scroll repositions it (or closes it when the anchor scrolls away).
 */
function ColumnFilterMenu({
  ariaLabel,
  options,
  selected,
  onChange,
}: {
  ariaLabel: string;
  options: TableColumnOption[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const positionPanel = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    if (r.bottom < 0 || r.top > window.innerHeight) {
      setOpen(false);
      return;
    }
    const panel = panelRef.current;
    const pw = panel?.offsetWidth ?? 224;
    const ph = panel?.offsetHeight ?? 256;
    let top = r.bottom + 4;
    if (top + ph > window.innerHeight - 8) {
      top = Math.max(8, r.top - ph - 4);
    }
    const left = Math.max(8, Math.min(r.right - pw, window.innerWidth - pw - 8));
    setPos({ top, left });
  }, []);

  // Clamp with the real panel size once it has mounted.
  useLayoutEffect(() => {
    if (open) positionPanel();
  }, [open, positionPanel]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || btnRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    window.addEventListener("scroll", positionPanel, true);
    window.addEventListener("resize", positionPanel);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", positionPanel, true);
      window.removeEventListener("resize", positionPanel);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, positionPanel]);

  const toggleOption = (value: string) => {
    onChange(
      selectedSet.has(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );
  };

  const active = selected.length > 0;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={`Filtrer på ${ariaLabel}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => {
          if (!open && btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            setPos({
              top: r.bottom + 4,
              left: Math.max(8, Math.min(r.right - 224, window.innerWidth - 232)),
            });
          }
          setOpen(!open);
        }}
        className={cn(
          "rounded p-0.5",
          active
            ? "text-admin-primary"
            : "text-admin-text-subtle/60 hover:text-admin-text-secondary",
        )}
      >
        <Filter className="h-3.5 w-3.5" />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed z-50 w-56 overflow-hidden rounded-md border border-admin-card-border bg-admin-card shadow-lg"
            style={{ top: pos.top, left: pos.left }}
          >
            <div className="max-h-64 overflow-y-auto p-1">
              {options.map((o) => {
                const isSelected = selectedSet.has(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => toggleOption(o.value)}
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-admin-text-primary hover:bg-admin-card-border/40"
                  >
                    <span
                      className={cn(
                        "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                        isSelected
                          ? "border-admin-primary-focus bg-admin-primary-focus/20"
                          : "border-admin-card-border",
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                    </span>
                    <span className="flex-1 truncate">{o.label}</span>
                    <span className="text-xs tabular-nums text-admin-text-subtle">
                      {o.count}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between border-t border-admin-card-border px-2 py-1.5">
              <span className="text-xs text-admin-text-subtle">
                {active ? `${selected.length} valgt` : "Alle"}
              </span>
              {active && (
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-xs text-admin-primary hover:underline"
                >
                  Nullstill
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
